import { debug, logError, logWarn } from 'lib/logging'

export type { IncomingCall } from 'app/generated/graphql'

/**
 * One same-origin WebSocket to `/events` for the whole authenticated app.
 *
 * The server authenticates from the browser cookies that ride the WS upgrade,
 * so there is nothing to send — `connect` only needs to know which workspace is
 * active, so it can drop any event that does not belong to it. Events are scoped
 * to a workspace on the wire, but a tab is only ever looking at one workspace at
 * a time; delivering another workspace's event to this tab would leak data
 * across the boundary, so we fail closed and never dispatch unless the event's
 * `workspaceId` matches the one we connected for.
 *
 * A module singleton, deliberately: every surface that wants live updates shares
 * this one socket rather than opening its own. React StrictMode double-invokes
 * effects in dev, so `connect`/`disconnect` are idempotent — a second `connect`
 * for the same workspace is a no-op, and `disconnect` without a socket does
 * nothing.
 */
export type StreamEvent = {
  name: string
  payload: unknown
  workspaceId: string
  locationId: string
}

type Handler = (event: StreamEvent) => void

// Reconnect only for drops we did not ask for. An explicit `disconnect` (logout,
// workspace switch, effect cleanup) clears this so the close handler leaves the
// socket down.
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 30000

const handlers = new Map<string, Set<Handler>>()

let socket: WebSocket | undefined
let activeWorkspaceId: string | undefined
// True only between `connect` and `disconnect`. The close handler uses it to
// tell an unexpected drop (reconnect) from a close we initiated (stay down).
let wantConnection = false
let reconnectAttempt = 0
let reconnectTimer: ReturnType<typeof setTimeout> | undefined

function eventsUrl(): string {
  const scheme = window.location.protocol === `https:` ? `wss:` : `ws:`
  return `${scheme}//${window.location.host}/events`
}

// A message is only an event if it has the exact shape. Anything else is either
// a protocol frame we don't handle or corruption, and dispatching it would hand
// subscribers a value they can't trust.
function parseEvent(data: unknown): StreamEvent | undefined {
  if (typeof data !== `string`) return undefined
  let raw: unknown
  try {
    raw = JSON.parse(data)
  } catch (err) {
    logWarn(`Event stream ignored a non-JSON message`, err instanceof Error ? err.message : String(err))
    return undefined
  }
  if (typeof raw !== `object` || raw === null) return undefined
  const candidate = raw as Record<string, unknown>
  if (typeof candidate.name !== `string`) return undefined
  if (typeof candidate.workspaceId !== `string`) return undefined
  if (typeof candidate.locationId !== `string`) return undefined
  if (!(`payload` in candidate)) return undefined
  return {
    name: candidate.name,
    payload: candidate.payload,
    workspaceId: candidate.workspaceId,
    locationId: candidate.locationId,
  }
}

function dispatch(event: StreamEvent): void {
  // Fail closed on cross-workspace events: a tab scoped to one workspace must
  // never see another's data, even if the server sends it.
  if (event.workspaceId !== activeWorkspaceId) {
    debug(`events`, `dropping event for a different workspace`, {
      name: event.name,
      eventWorkspaceId: event.workspaceId,
      activeWorkspaceId,
    })
    return
  }
  const subscribers = handlers.get(event.name)
  if (!subscribers || subscribers.size === 0) return
  // Iterate a copy so a handler that (un)subscribes from inside itself does not
  // mutate the set mid-iteration.
  for (const handler of Array.from(subscribers)) {
    try {
      handler(event)
    } catch (err) {
      // One subscriber failing must not stop the rest from seeing the event.
      logError(`Error in event stream subscriber`, { name: event.name }, err)
    }
  }
}

function clearReconnect(): void {
  if (reconnectTimer === undefined) return
  clearTimeout(reconnectTimer)
  reconnectTimer = undefined
}

function scheduleReconnect(): void {
  if (!wantConnection || activeWorkspaceId === undefined) return
  if (reconnectTimer !== undefined) return
  // Capped exponential backoff with full jitter, so a fleet of tabs reconnecting
  // after the same server blip does not stampede in lockstep.
  const capped = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS)
  const delay = Math.random() * capped
  reconnectAttempt += 1
  debug(`events`, `scheduling reconnect`, { attempt: reconnectAttempt, delay })
  reconnectTimer = setTimeout(() => {
    reconnectTimer = undefined
    openSocket()
  }, delay)
}

function openSocket(): void {
  if (activeWorkspaceId === undefined) return
  // Already have a live-or-opening socket; nothing to do.
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return

  const ws = new WebSocket(eventsUrl())
  socket = ws

  ws.onopen = () => {
    debug(`events`, `connected`, { workspaceId: activeWorkspaceId })
    // A clean open means the previous backoff series is done; the next drop
    // starts counting from zero again.
    reconnectAttempt = 0
    clearReconnect()
  }

  ws.onmessage = (msg: MessageEvent) => {
    const event = parseEvent(msg.data)
    if (!event) {
      debug(`events`, `ignoring unparseable message`)
      return
    }
    dispatch(event)
  }

  ws.onerror = (err) => {
    logError(`Event stream socket error`, err)
  }

  ws.onclose = (event) => {
    // Only react to the close of the socket we currently own; a stale handler
    // from a replaced socket must not touch state.
    if (socket !== ws) return
    socket = undefined
    if (!wantConnection) {
      debug(`events`, `closed after explicit disconnect`)
      return
    }
    debug(`events`, `unexpected close, will reconnect`, { code: event.code })
    scheduleReconnect()
  }
}

/**
 * Opens (or re-scopes) the shared socket for `workspaceId`.
 *
 * Idempotent for the same workspace: calling it twice — as StrictMode does —
 * leaves the one socket alone. A different workspace tears the old socket down
 * first, so events for the previous workspace can never arrive after the switch.
 */
export function connect(workspaceId: string): void {
  if (typeof __INTEGRATION_TEST__ !== `undefined` && __INTEGRATION_TEST__) return
  if (wantConnection && activeWorkspaceId === workspaceId) return
  if (activeWorkspaceId !== undefined && activeWorkspaceId !== workspaceId) {
    // Switching workspaces: drop the old socket cleanly before opening the new
    // one, and reset backoff so the switch does not inherit a stale delay.
    disconnect()
  }
  activeWorkspaceId = workspaceId
  wantConnection = true
  reconnectAttempt = 0
  openSocket()
}

/**
 * Explicit shutdown: logout, workspace switch, or effect cleanup. Clears the
 * reconnect intent so the close handler leaves the socket down.
 */
export function disconnect(): void {
  wantConnection = false
  activeWorkspaceId = undefined
  reconnectAttempt = 0
  clearReconnect()
  if (!socket) return
  const ws = socket
  socket = undefined
  // Drop the handlers first so this close cannot re-enter reconnect logic.
  ws.onopen = null
  ws.onmessage = null
  ws.onerror = null
  ws.onclose = null
  ws.close()
}

/**
 * Subscribes `handler` to events named `name`. Returns the unsubscribe.
 *
 * Subscriptions are independent of the socket lifecycle: a surface can subscribe
 * before `connect` and its handler will start receiving once events flow.
 */
export function subscribe(name: string, handler: Handler): () => void {
  let set = handlers.get(name)
  if (!set) {
    set = new Set()
    handlers.set(name, set)
  }
  set.add(handler)
  return () => {
    const current = handlers.get(name)
    if (!current) return
    current.delete(handler)
    if (current.size === 0) handlers.delete(name)
  }
}
